import React, { useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSearchResults, useSearchFilters } from '../contexts/SearchContext';
import { CompanyLogo } from './CompanyLogo';
import { CompanyName } from './CompanyName';

interface CompanyPageHeaderProps {
  companyId: string;
}

interface CompanyInfo {
  name: string;
  industry: string | null;
  jobCount: number;
  locations: string[];
  companyIdForLogo: string;
  isTyomarkkinatori: boolean;
}

export const CompanyPageHeader: React.FC<CompanyPageHeaderProps> = React.memo(({ companyId }) => {
  const intl = useIntl();
  const { results, isSearching } = useSearchResults();
  const { setFilters } = useSearchFilters();

  // Extract company information from search results
  const companyInfo: CompanyInfo | null = useMemo(() => {
    // Results is an Algolia response object with .hits array
    const hits = results?.hits || [];
    const nbHits = results?.nbHits || 0;
    
    // Need at least one hit to extract company details
    if (!hits || hits.length === 0) {
      return null;
    }

    // Get the first job to extract company details
    const firstJob = hits[0];
    
    // Extract unique locations from all jobs (location can be string or array)
    const locations = Array.from(
      new Set(
        hits
          .flatMap(job => {
            const loc = job.location;
            if (Array.isArray(loc)) return loc;
            if (typeof loc === 'string') return [loc];
            return [];
          })
          .filter(Boolean)
          .slice(0, 5) // Limit to top 5 locations
      )
    ) as string[];

    // Use companyId from job data if available, otherwise use the prop (which may be URL-encoded)
    // The logo component needs the actual companyId to construct the logo URL
    const actualCompanyId = firstJob.companyId || companyId;
    
    // Decode URL-encoded companyId if needed
    let decodedCompanyId = actualCompanyId;
    try {
      // Try to decode if it looks URL-encoded
      if (actualCompanyId.includes('%')) {
        decodedCompanyId = decodeURIComponent(actualCompanyId);
      } else if (actualCompanyId.includes('+')) {
        // Replace + with space (URL encoding for spaces)
        decodedCompanyId = actualCompanyId.replace(/\+/g, ' ');
      }
    } catch (e) {
      // If decoding fails, use original
      decodedCompanyId = actualCompanyId;
    }

    return {
      name: firstJob.company || decodedCompanyId,
      industry: firstJob.companyIndustry || null,
      jobCount: nbHits,
      locations,
      companyIdForLogo: decodedCompanyId,
      isTyomarkkinatori: firstJob.isTyomarkkinatori || false
    };
  }, [results, companyId]);

  // Don't render if no company info available
  if (!companyInfo) {
    return null;
  }

  // Get location count for display
  const locationCount = companyInfo?.locations.length || 0;

  // Handle clearing the company filter
  const handleClearFilter = () => {
    setFilters({});
  };

  return (
    <section id="company-info-header" className="company-info-header company-header-container mb-6" aria-labelledby="company-header-title">
      <div className="company-header-card bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 relative">
        {/* Close button */}
        <button
          onClick={handleClearFilter}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          aria-label="Clear company filter"
        >
          <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* Company Logo */}
          <div className="company-logo-wrapper flex-shrink-0">
            <CompanyLogo 
              companyName={companyInfo.companyIdForLogo}
              size={80}
              alt={companyInfo.name}
              variant="default"
              isTyomarkkinatori={companyInfo.isTyomarkkinatori}
            />
          </div>

          {/* Company Info */}
          <div className="flex-1 min-w-0 pr-8">
            <h1 id="company-header-title" className="company-name text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1">
              <CompanyName name={companyInfo.name} />
            </h1>
            
            <div className="company-meta flex flex-wrap items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
              {/* Industry */}
              {companyInfo.industry && (
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span className="font-medium">{companyInfo.industry}</span>
                </div>
              )}
              
              {/* Job Count */}
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="font-semibold text-blue-600 dark:text-blue-400">
                  <FormattedMessage
                    id="companyHeader.jobCount"
                    defaultMessage="{count, plural, one {# open position} other {# open positions}}"
                    description="Job count display in company page header"
                    values={{ count: companyInfo.jobCount }}
                  />
                </span>
              </div>

              {/* Location Count */}
              {locationCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>
                    <FormattedMessage
                      id="companyHeader.locationCount"
                      defaultMessage="{count, plural, one {# location} other {# locations}}"
                      description="Location count display in company page header"
                      values={{ count: locationCount }}
                    />
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Loading State Overlay */}
          {isSearching && (
            <div className="absolute inset-0 bg-white/50 dark:bg-gray-800/50 rounded-2xl flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
});

CompanyPageHeader.displayName = 'CompanyPageHeader';

